import { useEffect, useRef } from 'react'
import { useParams } from 'react-router-dom'

import { ScalrBreadcrumb } from '@/components/customComponents'
import FilterBar from '@/components/FilterBar'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'

import { FEATURE_MATRIX } from '@/config/features'
import { useKpiGoals } from '@/hooks'
import {
  addAlert,
  selectTenant,
  useAppDispatch,
  useAppSelector,
  useReduxBaseFilter,
} from '@/store'

import LaborPage from '@/screens/clarity/LaborPage'
import LoyaltyPage from '@/screens/clarity/LoyaltyPage'
import ProfitAndLossPage from '@/screens/clarity/PnlPage'
import SalesPage from '@/screens/clarity/SalesPage'
import SpeedOfServicePage from '@/screens/clarity/SpeedOfServicePage'
import SpendPage from '@/screens/clarity/SpendPage'
import { NotAllowedClarityPage } from '@/screens/error'

import { dateToString } from '@/utils'
import { addSampleAlerts } from '@/utils/alertDemo'

function PageContent() {
  const { id } = useParams()
  const tenant = useAppSelector(selectTenant)
  const { selectedBranch, date } = useReduxBaseFilter()
  const features = FEATURE_MATRIX[tenant]

  const { kpiGoals, loading: kpiGoalsLoading } = useKpiGoals(
    dateToString(date, 'from'),
    dateToString(date, 'to'),
    selectedBranch,
    tenant
  )

  return (
    <>
      <FilterBar pageId={id} />

      {id === 'sales' &&
        (features.claritySales ? (
          <SalesPage kpiGoals={kpiGoals} kpiGoalsLoading={kpiGoalsLoading} />
        ) : (
          <NotAllowedClarityPage pageTitle="Sales Clarity" />
        ))}

      {id === 'labor' &&
        (features.clarityLabor ? (
          <LaborPage kpiGoals={kpiGoals} kpiGoalsLoading={kpiGoalsLoading} />
        ) : (
          <NotAllowedClarityPage pageTitle="Labor Clarity" />
        ))}

      {id === 'spend' &&
        (features.claritySpend ? (
          <SpendPage kpiGoals={kpiGoals} kpiGoalsLoading={kpiGoalsLoading} />
        ) : (
          <NotAllowedClarityPage pageTitle="Spend Clarity" />
        ))}

      {id === 'speed-of-service' &&
        (features.claritySpeedOfService ? (
          <SpeedOfServicePage
            kpiGoals={kpiGoals}
            kpiGoalsLoading={kpiGoalsLoading}
          />
        ) : (
          <NotAllowedClarityPage pageTitle="Speed of Service Clarity" />
        ))}

      {id === 'loyalty' &&
        (features.clarityLoyalty ? (
          <LoyaltyPage kpiGoals={kpiGoals} kpiGoalsLoading={kpiGoalsLoading} />
        ) : (
          <NotAllowedClarityPage pageTitle="Loyalty Clarity" />
        ))}

      {id === 'profit-and-loss' &&
        (features.clarityProfitAndLoss ? (
          <ProfitAndLossPage />
        ) : (
          <NotAllowedClarityPage pageTitle="Profit & Loss Clarity" />
        ))}
    </>
  )
}

export default function CockpitPage() {
  const { id } = useParams()

  const dispatch = useAppDispatch()
  const hasInitialized = useRef(false)

  useEffect(() => {
    if (!hasInitialized.current) {
      addSampleAlerts({
        addAlert: (alert: Parameters<typeof addAlert>[0]) =>
          dispatch(addAlert(alert)),
      } as any)
      hasInitialized.current = true
    }
  }, [dispatch])

  return (
    <Card className="w-full">
      <CardHeader className="flex flex-row justify-between">
        <CardTitle>
          {id === 'sales' && 'Sales'}
          {id === 'labor' && 'Labor'}
          {id === 'spend' && 'Spend'}
          {id === 'speed-of-service' && 'Speed of Service'}
          {id === 'loyalty' && 'Loyalty'}
          {id === 'profit-and-loss' && 'Profit & Loss'}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <ScalrBreadcrumb
          breadcrumbItems={[{ label: 'Clarity', href: '/clarity/sales' }]}
          title={
            id === 'sales'
              ? 'Sales'
              : id === 'labor'
                ? 'Labor'
                : id === 'spend'
                  ? 'Spend'
                  : id === 'speed-of-service'
                    ? 'Speed of Service'
                    : id === 'loyalty'
                      ? 'Loyalty'
                      : 'Profit & Loss'
          }
        />

        <PageContent />
      </CardContent>
    </Card>
  )
}
